import type { ISerialize } from '@/objects/ISerialize';
import { ImportFormat } from './ImportFormat';
import { ContestantImportRow } from './ContestantImportRow';

export class ContestantImport implements ISerialize {
    public AutoselectCategory: boolean;

    public AutoselectRace: boolean;

    public Cols: string[];

    public CreateMissingBibs: boolean;

    public EventID: number;

    public Format: ImportFormat | null;

    public GenerateCategories: boolean;

    public GenerateGroups: boolean;

    public GenerateRacesForGroups: boolean;

    public GenerateTeams: boolean;

    public GUID: string | null;

    public IgnoreRowsWithErrors: boolean;

    public ImportEmpty: boolean;

    public readonly IsResultImport: boolean;

    public Locale: string | null;

    public OneTimeParticipant: boolean;

    public RelayTeamsOnly: boolean;

    public Rows: ContestantImportRow[];

    public SameParticipantForMulitpleRaces: boolean;

    public SkipExistingContestants: boolean;

    public UpdateParticipant: boolean;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        AutoselectCategory?: boolean;
        AutoselectRace?: boolean;
        Cols?: string[];
        CreateMissingBibs?: boolean;
        EventID?: number;
        Format?: ImportFormat | null;
        GenerateCategories?: boolean;
        GenerateGroups?: boolean;
        GenerateRacesForGroups?: boolean;
        GenerateTeams?: boolean;
        GUID?: string | null;
        IgnoreRowsWithErrors?: boolean;
        ImportEmpty?: boolean;
        IsResultImport?: boolean;
        Locale?: string | null;
        OneTimeParticipant?: boolean;
        RelayTeamsOnly?: boolean;
        Rows?: ContestantImportRow[];
        SameParticipantForMulitpleRaces?: boolean;
        SkipExistingContestants?: boolean;
        UpdateParticipant?: boolean;
    }) {
        this.AutoselectCategory = values.AutoselectCategory ?? false;

        this.AutoselectRace = values.AutoselectRace ?? false;

        this.Cols = values.Cols ?? [];

        this.CreateMissingBibs = values.CreateMissingBibs ?? false;

        this.EventID = values.EventID ?? 0;

        this.Format = values.Format ?? null;

        this.GenerateCategories = values.GenerateCategories ?? false;

        this.GenerateGroups = values.GenerateGroups ?? false;

        this.GenerateRacesForGroups = values.GenerateRacesForGroups ?? false;

        this.GenerateTeams = values.GenerateTeams ?? false;

        this.GUID = values.GUID ?? '';

        this.IgnoreRowsWithErrors = values.IgnoreRowsWithErrors ?? false;

        this.ImportEmpty = values.ImportEmpty ?? false;

        this.IsResultImport = values.IsResultImport ?? false;

        this.Locale = values.Locale ?? '';

        this.OneTimeParticipant = values.OneTimeParticipant ?? false;

        this.RelayTeamsOnly = values.RelayTeamsOnly ?? false;

        this.Rows = values.Rows ?? [];

        this.SameParticipantForMulitpleRaces = values.SameParticipantForMulitpleRaces ?? false;

        this.SkipExistingContestants = values.SkipExistingContestants ?? false;

        this.UpdateParticipant = values.UpdateParticipant ?? false;
    }

    public serialize(): string {
        return (
            '{' +
            `"AutoselectCategory": ${JSON.stringify(this.AutoselectCategory)}` +
            ',' +
            `"AutoselectRace": ${JSON.stringify(this.AutoselectRace)}` +
            ',' +
            `"Cols": [ ${this.Cols.map((x) => JSON.stringify(x))} ]` +
            ',' +
            `"CreateMissingBibs": ${JSON.stringify(this.CreateMissingBibs)}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"Format": ${!this.Format ? null : this.Format?.serialize()}` +
            ',' +
            `"GenerateCategories": ${JSON.stringify(this.GenerateCategories)}` +
            ',' +
            `"GenerateGroups": ${JSON.stringify(this.GenerateGroups)}` +
            ',' +
            `"GenerateRacesForGroups": ${JSON.stringify(this.GenerateRacesForGroups)}` +
            ',' +
            `"GenerateTeams": ${JSON.stringify(this.GenerateTeams)}` +
            ',' +
            `"GUID": ${JSON.stringify(this.GUID)}` +
            ',' +
            `"IgnoreRowsWithErrors": ${JSON.stringify(this.IgnoreRowsWithErrors)}` +
            ',' +
            `"ImportEmpty": ${JSON.stringify(this.ImportEmpty)}` +
            ',' +
            `"IsResultImport": ${JSON.stringify(this.IsResultImport)}` +
            ',' +
            `"Locale": ${JSON.stringify(this.Locale)}` +
            ',' +
            `"OneTimeParticipant": ${JSON.stringify(this.OneTimeParticipant)}` +
            ',' +
            `"RelayTeamsOnly": ${JSON.stringify(this.RelayTeamsOnly)}` +
            ',' +
            `"Rows": [ ${this.Rows.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"SameParticipantForMulitpleRaces": ${JSON.stringify(this.SameParticipantForMulitpleRaces)}` +
            ',' +
            `"SkipExistingContestants": ${JSON.stringify(this.SkipExistingContestants)}` +
            ',' +
            `"UpdateParticipant": ${JSON.stringify(this.UpdateParticipant)}` +
            '}'
        );
    }

    /**
     * Creates an instance of ContestantImport from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        AutoselectCategory?: boolean;
        AutoselectRace?: boolean;
        Cols?: string[];
        CreateMissingBibs?: boolean;
        EventID?: number;
        Format?: ImportFormat | null;
        GenerateCategories?: boolean;
        GenerateGroups?: boolean;
        GenerateRacesForGroups?: boolean;
        GenerateTeams?: boolean;
        GUID?: string | null;
        IgnoreRowsWithErrors?: boolean;
        ImportEmpty?: boolean;
        Locale?: string | null;
        OneTimeParticipant?: boolean;
        RelayTeamsOnly?: boolean;
        Rows?: ContestantImportRow[];
        SameParticipantForMulitpleRaces?: boolean;
        SkipExistingContestants?: boolean;
        UpdateParticipant?: boolean;
    }): ContestantImport {
        return new ContestantImport(values);
    }

    public static _FromServerJson(json: any): ContestantImport {
        return new ContestantImport({
            AutoselectCategory: json.AutoselectCategory,
            AutoselectRace: json.AutoselectRace,
            Cols: json.Cols,
            CreateMissingBibs: json.CreateMissingBibs,
            EventID: json.EventID,
            Format: json.Format ? ImportFormat._FromServerJson(json.Format) : null,
            GenerateCategories: json.GenerateCategories,
            GenerateGroups: json.GenerateGroups,
            GenerateRacesForGroups: json.GenerateRacesForGroups,
            GenerateTeams: json.GenerateTeams,
            GUID: json.GUID ?? '',
            IgnoreRowsWithErrors: json.IgnoreRowsWithErrors,
            ImportEmpty: json.ImportEmpty,
            IsResultImport: json.IsResultImport,
            Locale: json.Locale ?? '',
            OneTimeParticipant: json.OneTimeParticipant,
            RelayTeamsOnly: json.RelayTeamsOnly,
            Rows: json.Rows?.map((x: any) => ContestantImportRow._FromServerJson(x)),
            SameParticipantForMulitpleRaces: json.SameParticipantForMulitpleRaces,
            SkipExistingContestants: json.SkipExistingContestants,
            UpdateParticipant: json.UpdateParticipant,
        });
    }

    /**
     * Creates an instance of ContestantImport from another instance of ContestantImport.
     * @param other The ContestantImport you want to clone
     * @returns A cloned ContestantImport
     */
    public static FromOther(other: ContestantImport): ContestantImport {
        return ContestantImport._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ContestantImport.FromOther(this);
    }

    static NAME_OF_CLASS = 'ContestantImport';
}
